import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { FundProvider } from "@/contexts/FundContext";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import CallbackPage from "@/pages/CallbackPage";
import DashboardLayout from "@/components/DashboardLayout";

// Super Admin - Master
import TychiMaster from "@/pages/TychiMaster";
import AdminManagement from "@/pages/AdminManagement";
import AuditLogs from "@/pages/AuditLogs";
import FundManagement from "@/pages/FundManagement";
import CreateFund from "@/pages/CreateFund";
import FundDetail from "@/pages/FundDetail";
import AppSelector from "@/pages/AppSelector";
import OrganizationsPage from "@/pages/OrganizationsPage";
import SuperAdminOnboardingSubmissions from "@/pages/SuperAdminOnboardingSubmissions";
import RolesPermissionsList from "@/pages/RolesPermissionsList";
import RoleForm from "@/pages/RoleForm";

// Super Admin - Portal (fund-scoped)
import PortalDashboard from "@/pages/PortalDashboard";
import AdminInvestors from "@/pages/AdminInvestors";
import AddInvestor from "@/pages/AddInvestor";
import InvestorDetailPage from "@/pages/InvestorDetailPage";
import ComplianceListPage from "@/pages/ComplianceListPage";
import ComplianceDetailPage from "@/pages/ComplianceDetailPage";
import DocumentsPage from "@/pages/DocumentsPage";
import TransactionsPage from "@/pages/TransactionsPage";
import ReportingPage from "@/pages/ReportingPage";
import GovernancePage from "@/pages/GovernancePage";
import SupportManagementPage from "@/pages/SupportManagementPage";
import StatusPage from "@/pages/StatusPage";
import SystemConfigPage from "@/pages/SystemConfigPage";
import StatementTemplatesPage from "@/pages/StatementTemplatesPage";
import KnowledgeBankPage from "@/pages/KnowledgeBankPage";

// Admin (Fund Manager)
import AdminDashboard from "@/pages/AdminDashboard";
import AdminFundProfile from "@/pages/AdminFundProfile";

// Investor
import InvestorDashboard from "@/pages/InvestorDashboard";
import InvestorProfile from "@/pages/InvestorProfile";
import InvestorDocumentsPage from "@/pages/InvestorDocumentsPage";
import InvestorTransactions from "@/pages/InvestorTransactions";
import InvestorReportingPage from "@/pages/InvestorReportingPage";
import InvestorSupport from "@/pages/InvestorSupport";
import InvestorFundProfile from "@/pages/InvestorFundProfile";

// Prospective Client
import ProspectiveClientDashboard from "@/pages/prospective/ProspectiveClientDashboard";
import ProspectiveOnboardingForm from "@/pages/prospective/ProspectiveOnboardingForm";
import ProspectiveOnboardingReview from "@/pages/prospective/ProspectiveOnboardingReview";

// Website
import HomePage from "@/pages/Website/HomePage";
import PlatformPage from "@/pages/Website/PlatformPage";
import ModulesPage from "@/pages/Website/ModulesPage";
import TechnologyPage from "@/pages/Website/TechnologyPage";
import SecurityPage from "@/pages/Website/SecurityPage";
import AboutPage from "@/pages/Website/AboutPage";
import ContactPage from "@/pages/Website/ContactPage";
import ResourcesPage from "@/pages/Website/ResourcesPage";
import ResourceDetailPage from "@/pages/Website/ResourceDetailPage";
import PrivacyPolicyPage from "@/pages/Website/PrivacyPolicyPage";
import TermsOfUsePage from "@/pages/Website/TermsOfUsePage";

import { ThemeProvider } from "@/contexts/ThemeContext";
import "@/App.css";

// Centralised role → landing path map. org_admin shares the super_admin UI
// but lands on its own /org-admin/dashboard URL (TychiMaster view).
const ROLE_PATHS = {
  super_admin: "/super-admin",
  org_admin: "/org-admin/dashboard",
  admin: "/admin",
  investor: "/investor",
  prospective_client: "/prospective",
};

function RoleRedirect() {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={ROLE_PATHS[user.role] || "/login"} replace />;
}

function ProtectedRoute({ children, allowedRoles }) {
  const { user, isLoading, isAuthenticated } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={ROLE_PATHS[user.role] || "/login"} replace />;
  }
  return children;
}

// SA wrapper accepts both super_admin and org_admin — org_admin gets the same
// dashboard surface minus User Management + Organizations (hidden in sidebar
// and blocked at /api/users/* and /api/organizations/*).
function SA({ children }) {
  return (
    <ProtectedRoute allowedRoles={["super_admin", "org_admin"]}>
      <DashboardLayout>{children}</DashboardLayout>
    </ProtectedRoute>
  );
}

// Super-admin-only wrapper (excludes org_admin). Used by Roles & Permissions.
function SAOnly({ children }) {
  return (
    <ProtectedRoute allowedRoles={["super_admin"]}>
      <DashboardLayout>{children}</DashboardLayout>
    </ProtectedRoute>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Website Pages */}
      <Route path="/" element={<HomePage />} />
      <Route path="/platform" element={<PlatformPage />} />
      <Route path="/modules" element={<ModulesPage />} />
      <Route path="/technology" element={<TechnologyPage />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/resources" element={<ResourcesPage />} />
      <Route path="/resources/:slug" element={<ResourceDetailPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-use" element={<TermsOfUsePage />} />

      {/* Auth */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/callback" element={<CallbackPage />} />
      <Route path="/portal" element={<RoleRedirect />} />

      {/* Org Admin — same TychiMaster surface as Super Admin, just a dedicated
          landing URL. All deeper navigation reuses /super-admin/* routes since
          the SA wrapper now accepts both roles. */}
      <Route path="/org-admin/dashboard" element={<SA><TychiMaster /></SA>} />

      {/* Super Admin — Master Context */}
      <Route path="/super-admin" element={<SA><TychiMaster /></SA>} />
      <Route path="/super-admin/organizations" element={<SA><OrganizationsPage /></SA>} />
      <Route path="/super-admin/onboarding" element={<SA><SuperAdminOnboardingSubmissions /></SA>} />
      <Route path="/super-admin/funds" element={<SA><FundManagement /></SA>} />
      <Route path="/super-admin/funds/create" element={<SA><CreateFund /></SA>} />
      <Route path="/super-admin/funds/:fundId" element={<SA><FundDetail /></SA>} />
      <Route path="/super-admin/admins" element={<SA><AdminManagement /></SA>} />
      <Route path="/super-admin/roles-permissions" element={<SAOnly><RolesPermissionsList /></SAOnly>} />
      <Route path="/super-admin/roles-permissions/create" element={<SAOnly><RoleForm /></SAOnly>} />
      <Route path="/super-admin/roles-permissions/:roleId/edit" element={<SAOnly><RoleForm /></SAOnly>} />
      <Route path="/super-admin/audit" element={<SA><AuditLogs /></SA>} />
      <Route path="/super-admin/support" element={<SA><SupportManagementPage /></SA>} />
      <Route path="/super-admin/governance" element={<SA><GovernancePage /></SA>} />
      <Route path="/super-admin/knowledge-bank" element={<SA><KnowledgeBankPage /></SA>} />

      {/* Super Admin — App Selector */}
      <Route path="/super-admin/funds/:fundId/apps" element={<SA><AppSelector /></SA>} />

      {/* Super Admin — Portal Context (fund-scoped) */}
      <Route path="/super-admin/funds/:fundId/portal" element={<SA><PortalDashboard /></SA>} />
      <Route path="/super-admin/funds/:fundId/portal/investors" element={<SA><AdminInvestors /></SA>} />
      <Route path="/super-admin/funds/:fundId/portal/investors/add" element={<SA><AddInvestor /></SA>} />
      <Route path="/super-admin/funds/:fundId/portal/investors/:investorId" element={<SA><InvestorDetailPage /></SA>} />
      <Route path="/super-admin/funds/:fundId/portal/documents" element={<SA><DocumentsPage /></SA>} />
      <Route path="/super-admin/funds/:fundId/portal/transactions" element={<SA><TransactionsPage /></SA>} />
      <Route path="/super-admin/funds/:fundId/portal/reporting" element={<SA><ReportingPage /></SA>} />
      <Route path="/super-admin/funds/:fundId/portal/support" element={<SA><SupportManagementPage /></SA>} />
      <Route path="/super-admin/funds/:fundId/portal/governance" element={<SA><GovernancePage /></SA>} />
      <Route path="/super-admin/funds/:fundId/portal/status" element={<SA><StatusPage /></SA>} />
      <Route path="/super-admin/funds/:fundId/portal/config" element={<SA><SystemConfigPage /></SA>} />
      <Route path="/super-admin/funds/:fundId/portal/statement-templates" element={<SA><StatementTemplatesPage /></SA>} />
      <Route path="/super-admin/funds/:fundId/portal/audit" element={<SA><AuditLogs /></SA>} />
      <Route path="/super-admin/funds/:fundId/portal/knowledge-bank" element={<SA><KnowledgeBankPage /></SA>} />
      <Route path="/super-admin/funds/:fundId/portal/compliance/fatca" element={<SAOnly><ComplianceListPage key="fatca" section="fatca" /></SAOnly>} />
      <Route path="/super-admin/funds/:fundId/portal/compliance/fatca/:investorId" element={<SAOnly><ComplianceDetailPage key="fatca" section="fatca" /></SAOnly>} />
      <Route path="/super-admin/funds/:fundId/portal/compliance/crs" element={<SAOnly><ComplianceListPage key="crs" section="crs" /></SAOnly>} />
      <Route path="/super-admin/funds/:fundId/portal/compliance/crs/:investorId" element={<SAOnly><ComplianceDetailPage key="crs" section="crs" /></SAOnly>} />

      {/* Admin (Fund Manager) */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><AdminDashboard /></DashboardLayout></ProtectedRoute>} />
      <Route path="/admin/fund" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><AdminFundProfile /></DashboardLayout></ProtectedRoute>} />
      <Route path="/admin/fund/:fundId" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><FundDetail /></DashboardLayout></ProtectedRoute>} />
      <Route path="/admin/investors" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><AdminInvestors /></DashboardLayout></ProtectedRoute>} />
      <Route path="/admin/investors/add" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><AddInvestor /></DashboardLayout></ProtectedRoute>} />
      <Route path="/admin/investors/:investorId" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><InvestorDetailPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/admin/documents" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><DocumentsPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/admin/transactions" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><TransactionsPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/admin/reporting" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><ReportingPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/admin/support" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><SupportManagementPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/admin/knowledge-bank" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><KnowledgeBankPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/admin/compliance/fatca" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><ComplianceListPage key="fatca" section="fatca" /></DashboardLayout></ProtectedRoute>} />
      <Route path="/admin/compliance/fatca/:investorId" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><ComplianceDetailPage key="fatca" section="fatca" /></DashboardLayout></ProtectedRoute>} />
      <Route path="/admin/compliance/crs" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><ComplianceListPage key="crs" section="crs" /></DashboardLayout></ProtectedRoute>} />
      <Route path="/admin/compliance/crs/:investorId" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><ComplianceDetailPage key="crs" section="crs" /></DashboardLayout></ProtectedRoute>} />

      {/* Investor */}
      <Route path="/investor" element={<ProtectedRoute allowedRoles={["investor"]}><DashboardLayout><InvestorDashboard /></DashboardLayout></ProtectedRoute>} />
      <Route path="/investor/profile" element={<ProtectedRoute allowedRoles={["investor"]}><DashboardLayout><InvestorProfile /></DashboardLayout></ProtectedRoute>} />
      <Route path="/investor/documents" element={<ProtectedRoute allowedRoles={["investor"]}><DashboardLayout><InvestorDocumentsPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/investor/transactions" element={<ProtectedRoute allowedRoles={["investor"]}><DashboardLayout><InvestorTransactions /></DashboardLayout></ProtectedRoute>} />
      <Route path="/investor/reporting" element={<ProtectedRoute allowedRoles={["investor"]}><DashboardLayout><InvestorReportingPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/investor/support" element={<ProtectedRoute allowedRoles={["investor"]}><DashboardLayout><InvestorSupport /></DashboardLayout></ProtectedRoute>} />
      <Route path="/investor/knowledge-bank" element={<ProtectedRoute allowedRoles={["investor"]}><DashboardLayout><KnowledgeBankPage /></DashboardLayout></ProtectedRoute>} />

      {/* Prospective Client (restricted access until SA approves onboarding) */}
      <Route path="/prospective" element={<ProtectedRoute allowedRoles={["prospective_client"]}><DashboardLayout><ProspectiveClientDashboard /></DashboardLayout></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute allowedRoles={["prospective_client"]}><DashboardLayout><ProspectiveOnboardingForm /></DashboardLayout></ProtectedRoute>} />
      <Route path="/onboarding/review" element={<ProtectedRoute allowedRoles={["prospective_client"]}><DashboardLayout><ProspectiveOnboardingReview /></DashboardLayout></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute allowedRoles={["prospective_client"]}><DashboardLayout><InvestorSupport /></DashboardLayout></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <FundProvider>
            <AppRoutes />
            <Toaster richColors position="top-right" />
          </FundProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
